import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, MessageCircle, Calculator, Lock, Scale, Landmark, Award } from "lucide-react";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const HERO_IMG =
  "https://images.pexels.com/photos/6077297/pexels-photo-6077297.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lines = [
  { text: "Divórcio sem estratégia", gold: false },
  { text: "pode custar até 50%", gold: true },
  { text: "do seu patrimônio.", gold: false },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18, delayChildren: 0.35 } },
};
const lineReveal = {
  hidden: { y: "115%", rotate: 2 },
  show: { y: "0%", rotate: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const STATS = [
  { icon: ShieldCheck, value: "100%", label: "Sigilo absoluto" },
  { icon: Landmark, value: "ITCMD & IR", label: "Otimização tributária" },
  { icon: Scale, value: "Todo o Brasil", label: "Atendimento digital" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden hero-spotlight"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-[120%] object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1320]/70 via-[#0B1320]/80 to-[#0B1320]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1320]/90 via-transparent to-[#0B1320]/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-mono-num text-[11px] sm:text-xs uppercase tracking-[0.32em] text-amber-400/90 mb-8 flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          Divórcio e Proteção Patrimonial.
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display text-4xl sm:text-5xl lg:text-[4.2rem] font-bold tracking-tight leading-[1.12] text-slate-100 max-w-4xl"
          data-testid="hero-headline"
        >
          {lines.map((l, i) => (
            <span className="mask-line" key={i}>
              <motion.span variants={lineReveal} className={l.gold ? "gold-text italic" : ""}>
                {l.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.15 }}
          className="max-w-2xl mt-8 space-y-3.5"
          data-testid="hero-subheadline"
        >
          <p className="text-sm sm:text-base lg:text-lg text-slate-300/90 leading-relaxed">
            Protegemos seu patrimônio, estruturamos seus negócios e defendemos os interesses da sua
            família.
          </p>
          <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
            A <span className="text-amber-200 font-semibold">TSS – ADVOGADOS</span> oferece assessoria
            jurídica estratégica em Direito Empresarial e Direito de Família, com foco na proteção de
            bens, no planejamento sucessório, no divórcio, na partilha de bens, na guarda dos filhos e
            nos alimentos.
          </p>
          <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
            Atendemos em todo o Brasil.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.35 }}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          <motion.a
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            href={waLink(WA_MESSAGES.hero)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-primary"
            className="inline-flex items-center justify-center gap-2.5 gold-surface text-[#0B1320] font-bold text-sm sm:text-base px-8 py-4 rounded-full shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:shadow-[0_0_60px_rgba(212,175,55,0.55)] transition-shadow duration-300"
          >
            <ShieldCheck className="w-5 h-5" />
            Proteger Meu Patrimônio Agora
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            href="#simulador"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2.5 border border-amber-400/40 text-amber-200 font-semibold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-amber-400/10 hover:border-amber-300 transition-colors duration-300"
          >
            <Calculator className="w-5 h-5" />
            Simular Risco Patrimonial
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.5 }}
          data-testid="hero-authority-badge"
          className="inline-flex items-center gap-3 border border-amber-400/30 bg-amber-400/5 rounded-full px-5 py-2.5 mt-8"
        >
          <Award className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-xs sm:text-sm text-amber-100/90">
            Liderado pelo <span className="font-semibold text-amber-200">Dr. Tiago Santo</span>
            <span className="font-mono-num text-[11px] sm:text-xs text-amber-400/90"> · OAB/MS 26.983</span>
            <span className="text-slate-400"> · +5 anos em divórcio &amp; proteção patrimonial</span>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 max-w-3xl"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-3.5 border border-amber-500/15 bg-[#111C2E]/60 backdrop-blur-md rounded-xl px-5 py-4"
            >
              <s.icon className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="font-mono-num text-sm font-semibold text-slate-100">{s.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 0.8 }}
          className="mt-10 flex items-center gap-2 text-xs text-slate-500"
        >
          <Lock className="w-3.5 h-3.5 text-amber-500/70" />
          Consulta inicial confidencial · Nenhum dado compartilhado sem autorização
        </motion.p>
      </motion.div>
    </section>
  );
};
